import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DiagnosticEngine } from '../../../services/DiagnosticEngine';
import { ReportGenerator } from '../../../services/ReportGenerator';
import { GradingEngine } from '../../../services/GradingEngine';

const Reading2Results = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summary');
  const [submission, setSubmission] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);

  useEffect(() => {
    const localSubs = JSON.parse(localStorage.getItem('llap_teacher_submissions') || '[]');
    
    // Filter strictly for Reading Mock 2 released or directly released submissions
    const releasedSubs = localSubs.filter(s => 
      (s.examId === 'reading-2' || s.examId === 'R2' || s.assessmentName?.includes('Mock Test 2')) && 
      (s.status === 'Released' || s.status === 'Verified' || s.reviewStatus === 'APPROVED' || s.reviewStatus === 'DIRECT_INSTRUCTOR_STUDENT_RELEASE' || s.directRelease === true)
    );

    let foundSub = null;
    if (releasedSubs.length > 0) {
      releasedSubs.sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
      foundSub = releasedSubs[0];
    }

    setSubmission(foundSub || null);
    if (foundSub) {
      const diag = DiagnosticEngine.analyze(foundSub);
      // Dynamically recalculate band using GradingEngine to avoid stale cache band values (e.g. 40/40 -> Band 9.0)
      const correctCount = diag.correctCount || foundSub.rawScore || 0;
      const computedBandNum = GradingEngine.convertToIELTSBand(correctCount);
      foundSub.estimatedBand = `Band ${computedBandNum}`;
      setDiagnostics(diag);
    }
  }, []);

  if (!submission || !diagnostics) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
        <h1 style={{ color: '#856404' }}>Result Pending Verification (Mock 2)</h1>
        <div style={{ background: '#fff3cd', border: '1px solid #ffeeba', padding: '20px', borderRadius: '8px', color: '#856404', marginTop: '20px' }}>
          <p><strong>Notice:</strong> Your Reading Mock Test 2 submission is either pending teacher review or awaiting automated verification processing.</p>
          <p>Once verified and released, the complete Student Results Hub will become accessible here.</p>
        </div>
        <button onClick={() => navigate('/dashboard')} style={{ marginTop: '20px', padding: '10px 20px', background: '#0056b3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Return to Dashboard
        </button>
      </div>
    );
  }

  // Strict Lifecycle Gate: Enforce that only released, approved, or directly released submissions can be viewed
  const isReleased = submission.status === 'Released' || 
                     submission.status === 'Verified' || 
                     submission.reviewStatus === 'APPROVED' || 
                     submission.reviewStatus === 'DIRECT_INSTRUCTOR_STUDENT_RELEASE' || 
                     submission.directRelease === true;

  if (!isReleased) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
        <h1 style={{ color: '#856404' }}>Access Restricted: Result Pending Approval (Mock 2)</h1>
        <div style={{ background: '#fff3cd', border: '1px solid #ffeeba', padding: '20px', borderRadius: '8px', color: '#856404', marginTop: '20px' }}>
          <p><strong>Notice:</strong> This report has not yet completed the official teacher approval or automated verification workflow.</p>
        </div>
        <button onClick={() => navigate('/dashboard')} style={{ marginTop: '20px', padding: '10px 20px', background: '#0056b3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Return to Dashboard
        </button>
      </div>
    );
  }

  // Dynamic band computation ensuring 39-40 correctly yields Band 9.0
  const liveBandValue = GradingEngine.convertToIELTSBand(diagnostics.correctCount);
  const displayBand = `Band ${liveBandValue}`;

  // Determine appropriate messaging status based on release pathway
  const isDirectRelease = submission.reviewStatus === 'DIRECT_INSTRUCTOR_STUDENT_RELEASE' || submission.directRelease === true;
  const statusDisplayLabel = isDirectRelease ? 'Result verified and released' : 'Released';

  return (
    <div style={{ padding: '30px', maxWidth: '1100px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #eaeaea', paddingBottom: '20px', marginBottom: '30px' }}>
        <div>
          <h1 style={{ color: '#0056b3', margin: '0 0 5px 0' }}>LLAP Academy — Student Results Hub (Reading Mock 2)</h1>
          <p style={{ color: '#666', margin: 0 }}>Path: <code>performance/mock exam/reading-2</code> | Status: <strong style={{ color: '#28a745' }}>{statusDisplayLabel}</strong></p>
        </div>
        <button onClick={() => navigate('/dashboard')} style={{ padding: '8px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Return to Dashboard</button>
      </header>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button style={{ padding: '10px 18px', background: activeTab === 'summary' ? '#0056b3' : '#fff', color: activeTab === 'summary' ? '#fff' : '#333', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => setActiveTab('summary')}>Performance Summary</button>
        <button style={{ padding: '10px 18px', background: activeTab === 'review' ? '#0056b3' : '#fff', color: activeTab === 'review' ? '#fff' : '#333', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => setActiveTab('review')}>Itemized Answer Review</button>
        <button onClick={() => ReportGenerator.generateStudentPDF({ ...submission, estimatedBand: displayBand }, diagnostics)} style={{ padding: '10px 18px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Download PDF Report</button>
      </div>

      {activeTab === 'summary' ? (
        <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ddd' }}>
          <h2>Reading Mock 2 Performance Overview</h2>
          <p><strong>Overall Band Score:</strong> {displayBand}</p>
          <p><strong>Raw Score:</strong> {diagnostics.correctCount} / {diagnostics.totalQuestions}</p>
          <p><strong>Feedback Status:</strong> {submission.teacherFeedback || (isDirectRelease ? 'Result verified and released successfully.' : 'Verified and released by supervisor.')}</p>
        </div>
      ) : (
        <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ddd' }}>
          <h2>Itemized Answer Review (Mock 2)</h2>
          {(submission.results || []).map((q, idx) => (
            <div key={idx} style={{ borderBottom: '1px solid #eee', padding: '10px 0' }}>
              <p><strong>Question {idx + 1}:</strong> Your Answer: {q.studentAnswer || 'N/A'} | Correct Answer: {q.correctAnswer} | Result: <span style={{ color: q.isCorrect ? '#166534' : '#991b1b', fontWeight: 'bold' }}>{q.isCorrect ? 'Correct' : 'Incorrect'}</span></p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Reading2Results;